import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CtaBanner } from "@/components/site/CtaBanner";
import { Reveal } from "@/components/site/Reveal";
import { CheckCircle2, ChevronRight, ArrowRight, Phone } from "lucide-react";
import { findItem, type UsageItem } from "@/lib/usage-data";

export const Route = createFileRoute("/usage/$category/$item")({
  loader: ({ params }) => {
    const found = findItem(params.category, params.item);
    if (!found) throw notFound();
    return found;
  },
  head: ({ loaderData, params }) => {
    const title = loaderData ? `${loaderData.item.name} Epoxy Flooring NJ | Blanco Pro` : "Usage";
    const desc = loaderData?.item.description.slice(0, 155) ?? "";
    const url = `https://blancopros.com/usage/${params.category}/${params.item}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: "https://blancopros.com/og-image.jpg" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: "https://blancopros.com/og-image.jpg" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: loaderData
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://blancopros.com/" },
                  { "@type": "ListItem", position: 2, name: "Usage", item: "https://blancopros.com/usage" },
                  { "@type": "ListItem", position: 3, name: loaderData.category.name, item: `https://blancopros.com/usage/${params.category}` },
                  { "@type": "ListItem", position: 4, name: loaderData.item.name, item: url },
                ],
              }),
            },
          ]
        : [],
    };
  },
  notFoundComponent: () => (
    <div className="pt-40 pb-20 text-center px-4">
      <h1 className="font-display text-4xl font-bold mb-4">Project type not found</h1>
      <Link to="/usage" className="text-foreground underline">Back to Usage</Link>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="pt-40 pb-20 text-center px-4">
      <h1 className="font-display text-4xl font-bold mb-4">Something went wrong</h1>
      <button onClick={reset} className="text-foreground underline">Try again</button>
    </div>
  ),
  component: ItemPage,
});

function ItemPage() {
  const { category, item } = Route.useLoaderData();
  const siblings = category.items.filter((i: UsageItem) => i.slug !== item.slug).slice(0, 4);

  return (
    <>
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-hero">
        <div className="max-w-7xl mx-auto">
          <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1 text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/usage" className="hover:text-foreground transition-colors">Usage</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/usage/$category" params={{ category: category.slug }} className="hover:text-foreground transition-colors">
              {category.name}
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground">{item.name}</span>
          </nav>

          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">{category.name} Epoxy</div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.05] mb-6">
              {item.name} <span className="bg-gradient-gold bg-clip-text text-transparent">epoxy flooring.</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl">{item.blurb}</p>
          </Reveal>
        </div>
      </section>

      {item.image && (
        <section className="px-4 sm:px-6 lg:px-8 -mt-4">
          <div className="max-w-6xl mx-auto">
            <Reveal>
              <img
                src={item.image}
                alt={`${item.name} epoxy flooring by Blanco Pro Services`}
                width={1600}
                height={1024}
                loading="lazy"
                className="w-full rounded-3xl shadow-elegant object-cover aspect-[16/10]"
              />
            </Reveal>
          </div>
        </section>
      )}

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <Reveal>
              <h2 className="font-display text-3xl font-bold mb-6">About this application</h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">{item.description}</p>
              <h3 className="font-display text-xl font-semibold mb-4">Key benefits</h3>
              <ul className="space-y-3">
                {item.benefits.map((b: string) => (
                  <li key={b} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 text-foreground shrink-0" />
                    <span className="text-foreground/90">{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <aside className="lg:col-span-2">
            <Reveal delay={100}>
              <div className="sticky top-28 rounded-3xl border border-border bg-card p-8 shadow-soft">
                <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Free Estimate</div>
                <h3 className="font-display text-2xl font-bold mb-3">Get a quote for your {item.name.toLowerCase()}.</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Licensed, insured, and serving New Jersey, Pennsylvania, and New York.
                </p>
                <a
                  href="tel:9088956133"
                  className="w-full inline-flex items-center justify-center gap-2 bg-foreground text-background px-5 py-3 rounded-full text-sm font-medium hover:bg-gradient-gold hover:text-gold-foreground transition-all mb-3"
                >
                  <Phone className="w-4 h-4" />
                  908-895-6133
                </a>
                <Link
                  to="/contact"
                  className="w-full inline-flex items-center justify-center gap-2 border-2 border-foreground px-5 py-3 rounded-full text-sm font-medium hover:bg-foreground hover:text-background transition-all"
                >
                  Request a Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      {siblings.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <Reveal>
              <h2 className="font-display text-3xl font-bold mb-8">More {category.name.toLowerCase()} applications</h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {siblings.map((s: UsageItem, i: number) => (
                <Reveal key={s.slug} delay={i * 60}>
                  <Link
                    to="/usage/$category/$item"
                    params={{ category: category.slug, item: s.slug }}
                    className="group block h-full rounded-2xl border border-border bg-card p-5 hover:shadow-elegant hover:-translate-y-1 transition-all"
                  >
                    <h3 className="font-display text-lg font-bold mb-2">{s.name}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{s.blurb}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-foreground/70 group-hover:gap-2 transition-all">
                      View <ArrowRight className="w-3 h-3" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBanner />
    </>
  );
}
